import { RotateCw } from 'lucide-react'
import { Button } from '../ui/Button.js'
import { CopyButton } from '../ui/CopyButton.js'
import { EmptyState } from '../data/EmptyState.js'
import { Row } from '../layout/Stack.js'
import { Text } from '../ui/Typography.js'

export interface ErrorStateProps {
  /** The server's own message where there is one. It knows what went wrong. */
  message?: string | null
  /**
   * The technical line, for a crash rather than a failed request.
   *
   * Kept and shown small, with something to copy it: *"Rendered more hooks
   * than during the previous render"* is the one string that says what
   * happened, and it is not an explanation to lead with. The alternative to
   * showing it is a photograph of a screen, which is what people send.
   */
  detail?: string | null
  onRetry?: () => void
}

/**
 * A screen that could not load.
 *
 * *"Not your fault, and nothing has been charged or lost."* The product's wording
 * for the failure with no screen of its own, and a Try again rather than a dead end.
 */
export function ErrorState({ message, detail, onRetry }: ErrorStateProps) {
  return (
    <EmptyState
      title="That did not load"
      description={
        <>
          {message ||
            'Not your fault, and nothing has been changed. Try again, and if it keeps happening, tell us and we will look.'}
          {detail && (
            <Row gap={2} className="mt-3 justify-center">
              <Text as="span" variant="caption" className="text-ink-muted font-mono">
                {detail}
              </Text>
              <CopyButton value={detail} what="what went wrong" />
            </Row>
          )}
        </>
      }
      action={
        onRetry && (
          <Button variant="secondary" onClick={onRetry} leadingIcon={<RotateCw />}>
            Try again
          </Button>
        )
      }
    />
  )
}
